import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import UseAlert from '../../components/common/useAlerte'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { addAudit } from '../../redux/audits/auditActions'
import SelectMission from '../../components/common/SelectMission'
import QuickCreate, { QuickCreated, useQuickCreate } from '../../components/quickCreate/QuickCreate'
import { QuickEntity } from '../../components/quickCreate/quickCreateErrors'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { createSanitizedObjectHandler } from '../../utils/inputHandlers'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

const AuditCreate = () => {
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [audit, setAudit] = useState({
    AuditName: '',
    Description: '',
    MissionId: '',
  })

  const status = useSelector(
    (state: RootState) => state.audit?.addedAudit?.status
  )
  const message = useSelector(
    (state: RootState) => state.audit?.addedAudit?.message
  )

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    status: 0,
  })

  const handleInputChange = createSanitizedObjectHandler(setAudit, {
    AuditName: 'text',
    Description: 'text',
  })

  const qc = useQuickCreate()
  const handleQuickCreated = (kind: QuickEntity, created: QuickCreated) => {
    qc.close()
    qc.bump(kind)
    if (kind === 'mission' && created._id) {
      setAudit((prev) => ({ ...prev, MissionId: created._id as string }))
    }
  }

  const handleMissionChange = (newMission: string) => {
    setAudit((prev) => ({
      ...prev,
      MissionId: newMission,
    }))
  }

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }))
    if (alert.status === 200) {
      navigate('/audits')
    }
  }

  const submitLabel = t('buttons.add')

  const handleAddAudit = async () => {
    const { AuditName, Description, MissionId } = audit

    if (AuditName && Description && MissionId) {
      await dispatch(addAudit(audit))
      setAudit({ AuditName: '', Description: '', MissionId: '' })

      setAlert({
        open: true,
        message: message || t('alerts.auditAdd.success'),
        status: status || 200,
      })
    } else {
      setAlert({
        open: true,
        message: t('alerts.auditAdd.emptyFields'),
        status: 0,
      })
    }
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.audit.createEyebrow', {
          defaultValue: 'Sources · Audits',
        })}
        title={t('buttons.addAudit', { defaultValue: 'New audit' })}
        subtitle={t('entityForms.audit.createSubtitle', {
          defaultValue:
            'Create an audit linked to a mission for use as a task source.',
        })}
        backLabel={t('entityForms.audit.backToList', {
          defaultValue: 'Back to audits',
        })}
        onBack={() => navigate('/audits')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        status={alert.status}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.audit.sectionTitle', {
                defaultValue: 'Audit details',
              })}
              description={t('entityForms.audit.sectionDesc', {
                defaultValue:
                  'These fields identify the audit when it appears as a task source.',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Audit Name')} required>
                  <input
                    className="incyia-form-inp"
                    name="AuditName"
                    value={audit.AuditName}
                    onChange={handleInputChange as any}
                    required
                  />
                </FormField>
                <FormField label={t('Mission')} required>
                  <SelectMission
                    value={audit.MissionId}
                    onChange={handleMissionChange}
                    onCreateNew={qc.open('mission')}
                    reloadKey={qc.keyOf('mission')}
                  />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={audit.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('buttons.cancel', { defaultValue: 'Cancel' })}
              submitLabel={submitLabel}
              onCancel={() => navigate('/audits')}
              onSubmit={handleAddAudit}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.audit.createTip', {
              defaultValue:
                'Link the audit to a mission so it can be used as a task source in reports.',
            })}
            steps={[
              {
                title: t('entityForms.audit.sectionTitle', {
                  defaultValue: 'Audit details',
                }),
                fieldCount: 3,
              },
            ]}
            requiredCount={3}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
      <QuickCreate request={qc.request} onClose={qc.close} onCreated={handleQuickCreated} />
    </div>
  )
}

export default AuditCreate
